import React, { useEffect, useId, useRef, useState } from 'react';
import { Button } from 'antd';

import ColorPalette from './ColorPalette';
import CustomColorPicker from './CustomColorPicker';

/**
 * What opens from the `color` element's input and the profile's avatar color
 * (Input.js InputColor), in place of antd's gradient panel (KPR-325): the same
 * palette and the same custom panel as the icon + color modal, so there is one
 * color picker to learn. It is handed to antd's ColorPicker through
 * `panelRender`, which keeps antd's trigger, popup placement and status
 * styling; the panel itself is ours, because antd's presets are 24px
 * mouse-only blocks that a keyboard cannot reach.
 *
 * `color` is the stored "#rrggbb" or '' (nothing chosen); `onPick(hex)` and
 * `onClear()` are the only ways out. Custom opens closed, like the modal's,
 * and Escape closes the popup and gives focus back to the field that opened it
 * (`onClose`), unless a typed field has a half-entry that Escape is reverting
 * (CustomColorPicker stops that one).
 */
export default function ColorInputPanel({ color, onPick, onClear = null, onClose, phone = false }) {
	const [customOpen, setCustomOpen] = useState(false);
	const customPanelId = useId();
	const root = useRef(null);

	// A popup opened from the keyboard has nothing to Tab into (it is a portal at
	// the end of the page), so the first swatch takes focus when it opens.
	// antd mounts the popup hidden and reveals it a frame later, and a hidden
	// element refuses focus, so it is retried for a few frames rather than
	// assumed to land on the first.
	useEffect(() => {
		let frame = 0;
		let tries = 0;
		const focusFirst = () => {
			const first = root.current?.querySelector('button');
			first?.focus({ preventScroll: true });
			if (first && document.activeElement !== first && tries++ < 10) frame = requestAnimationFrame(focusFirst);
		};
		focusFirst();
		return () => cancelAnimationFrame(frame);
	}, []);

	const handleKeyDown = event => {
		if (event.key === 'Escape') {
			event.stopPropagation();
			onClose();
		}
	};

	return (
		<div
			ref={root}
			className={`color-input-panel${phone ? ' color-input-panel--phone' : ''}`}
			role='group'
			aria-label='Choose a color'
			onKeyDown={handleKeyDown}
		>
			<ColorPalette
				color={color}
				onPick={onPick}
				customOpen={customOpen}
				onToggleCustom={() => setCustomOpen(wasOpen => !wasOpen)}
				customPanelId={customPanelId}
			/>
			{customOpen ? (
				<CustomColorPicker id={customPanelId} color={color} onChange={onPick} phone={phone} />
			) : null}
			{onClear ? (
				<Button className='color-input-panel__clear' onClick={onClear} disabled={!color}>
					Clear color
				</Button>
			) : null}
		</div>
	);
}
